<template>
	<v-dialog :model-value="phase === 'assigning'" width="600px" persistent>
		<select-dtm-ortho-type-dialog v-if="files" :files="files" :initial-distance-unit="initialDistanceUnit"
			@confirm="onConfirm" @previous="emit('previous')" @close="emit('cancel')" />
	</v-dialog>
</template>

<script setup lang="ts">
import SelectDtmOrthoTypeDialog from "@/components/AddData/dtm-ortho/SelectDtmOrthoTypeDialog.vue";
import type {
	DtmOrthoFilePair,
	DtmOrthoImportInput,
	DtmOrthoImportSelection,
} from "@/components/AddData/dtm-ortho/dtmOrtho.types";
import { resolveAssignment } from "@/components/AddData/dtm-ortho/dtmOrthoImport";
import { useDtmOrthoUpload } from "@/components/AddData/dtm-ortho/useDtmOrthoUpload";
import { onMounted, watch } from "vue";
import { useRoute } from "vue-router";

/**
 * DTM / オルソの種別選択ダイアログを host する、取込元を問わないフロー（手動 / Groupware / Dashboard）。
 * 親は組が揃ったときだけマウントし、previous / cancel / finished で閉じる。
 * facade（useI18n / useStore を呼ぶ）はこの子だけが持つ（GI-17）
 */
const props = withDefaults(
	defineProps<{
		/** 取込対象の 2 件（選択順。1 件目が画像の既定になる。親 AC-7）。origin は問わない（GI-11） */
		pair: DtmOrthoFilePair;
		/**
		 * true: 確定で facade が取込を開始し、done / failed で finished を emit する。
		 * false: 取込は行わず、origin 付きの割り当てを confirm で親に渡す（Dashboard の一括取込。GI-20）
		 */
		uploadOnConfirm?: boolean;
	}>(),
	{ uploadOnConfirm: true },
);

const emit = defineEmits<{
	/** 「前へ」。親は組を保持したまま前の画面に戻す */
	(e: "previous"): void;
	/** 「キャンセル」。親はフロー全体を閉じる */
	(e: "cancel"): void;
	/** uploadOnConfirm=false のときの確定。親が取込を行う */
	(e: "confirm", input: DtmOrthoImportInput): void;
	/** 取込が受け付けられた、または失敗した（uploadOnConfirm=true のみ）。親はフロー全体を閉じる */
	(e: "finished"): void;
}>();

// route はテスト等で provider が無い場合 undefined になるため素通しできる形で読む。
// 現場切替に追従するよう getter で渡す
const route = useRoute();
const getSiteId = (): string | undefined => {
	const siteId = route?.query?.siteId;
	return typeof siteId === "string" ? siteId : undefined;
};

const { phase, files, initialDistanceUnit, start, assign } =
	useDtmOrthoUpload(getSiteId);

onMounted(() => {
	start(props.pair);
});

const onConfirm = (selection: DtmOrthoImportSelection): void => {
	if (props.uploadOnConfirm) {
		void assign(selection);
		return;
	}
	emit("confirm", {
		assignment: resolveAssignment(props.pair, selection),
		distanceUnit: selection.distanceUnit,
	});
};

// facade は done / failed から idle に戻らないため、ここを閉じる契機にする
watch(phase, (next) => {
	if (next === "done" || next === "failed") emit("finished");
});
</script>
